import { computed, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'

import { useReportDateRange } from '@/composables/useReportDateRange'
import { reportingMerchantId } from '@/helpers/franchiseMerchant'
import { formatDollars } from '@/helpers/money'
import { normalizeRangeValue } from '@/helpers/revenueRange'
import api from '@/store/api'

// Franchise endpoints answer plain JSON, not JSONAPI objects, so nothing goes into the object cache.
const NO_CACHE = { receive: false }

const get = path => api.call('GET', path, {}, NO_CACHE).then(response => response.data)

const rangeQuery = ({ beginning_date, ending_date }) => `beginning_date=${beginning_date}&ending_date=${ending_date}`
const merchantQuery = merchantId => (merchantId ? `&merchant_ids[]=${merchantId}` : '')

const EMPTY_DAY = { total: null, orders: 0, pickups: 0, subscribers: 0, warning: false, locations: null }

const calendarCell = (date, day) =>
  day
    ? {
        ...EMPTY_DAY,
        date,
        total: formatDollars(day.total_cents / 100),
        orders: day.orders,
        locations: { with: day.locations_with_orders, of: day.locations },
        clickable: day.orders > 0
      }
    : { ...EMPTY_DAY, date, clickable: false }

// Same idea as DynamicReport's latestFetch: each run takes a token, and only the
// newest run's answers (or failures) are applied, so a slow earlier response never wins.
const latestOnly = () => {
  let latest = 0
  return { next: () => ++latest, isCurrent: token => token === latest }
}

// Fetches path and hands the data (or `empty` on failure) to apply while token is newest.
const fetchLatest = (guard, token, path, apply, empty, setLoading) => {
  setLoading(true)
  return get(path)
    .then(data => guard.isCurrent(token) && apply(data))
    .catch(() => guard.isCurrent(token) && apply(empty))
    .finally(() => guard.isCurrent(token) && setLoading(false))
}

export function useFranchiseDashboard() {
  const store = useStore()
  const range = useReportDateRange('30d')
  const activeMerchantId = computed(() => reportingMerchantId(store.state))

  const loading = reactive({ summary: false, sales: false, leaderboard: false, calendar: false })
  const summary = ref(null)
  const salesRows = ref([])
  const seriesOptions = ref([])
  const leaderboard = ref([])
  const productionDays = ref([])
  const visibleDates = ref([])
  const calendarCurrent = ref(new Date())

  const reports = latestOnly()
  const calendar = latestOnly()

  const section = (token, key, path, apply, empty) =>
    fetchLatest(reports, token, path, apply, empty, value => (loading[key] = value))

  const load = () => {
    const token = reports.next()
    const merchantId = activeMerchantId.value
    const query = rangeQuery(range.apiDateRange())
    const scoped = query + merchantQuery(merchantId)
    const series = merchantId ? 'store' : 'merchant'

    const requests = [
      section(token, 'summary', `franchise/reports/sales_summary?${scoped}`, data => (summary.value = data), null),
      section(
        token,
        'sales',
        `franchise/reports/sales?${scoped}&series=${series}`,
        data => {
          salesRows.value = data.rows ?? []
          seriesOptions.value = data.series_options ?? []
        },
        {}
      )
    ]
    // The leaderboard is franchise-wide and only shown when no single location is picked.
    if (!merchantId) {
      requests.push(
        section(
          token,
          'leaderboard',
          `franchise/reports/leaderboard?${query}`,
          data => (leaderboard.value = data.rows ?? []),
          {}
        )
      )
    }
    return Promise.all(requests)
  }

  const setRange = value => {
    range.setDateRange(normalizeRangeValue(value))
    return load()
  }

  // The calendar emits its visible range; kept so a location change can refetch the same weeks.
  let calendarRange = null

  const loadCalendar = ({ from, to, dates }) => {
    const token = calendar.next()
    calendarRange = { from, to, dates }
    visibleDates.value = dates
    return fetchLatest(
      calendar,
      token,
      `franchise/production_days?date_from=${from}&date_to=${to}${merchantQuery(activeMerchantId.value)}`,
      data => (productionDays.value = data.days ?? []),
      {},
      value => (loading.calendar = value)
    )
  }

  const days = computed(() => {
    const byDate = Object.fromEntries(productionDays.value.map(day => [day.date, day]))
    return visibleDates.value.map(date => calendarCell(date, byDate[date]))
  })

  watch(activeMerchantId, () => {
    load()
    if (calendarRange) loadCalendar(calendarRange)
  })

  return {
    range,
    setRange,
    activeMerchantId,
    loading,
    summary,
    salesRows,
    seriesOptions,
    leaderboard,
    days,
    calendarCurrent,
    load,
    loadCalendar
  }
}
